import React, {
  CSSProperties,
  ReactNode,
  Suspense,
  forwardRef,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import mergeRefs from "react-merge-refs";
import { ThemeProvider } from "styled-components";
import { FlexContainer, Icon, Text, TextBold, TextField } from "ui";
import AngleDown from "core/icons/angle/angle-down.svg";
import AngleUp from "core/icons/angle/angle-up.svg";
import ChecklistIcon from "core/icons/checklist/checklist.svg";
import CrossIcon from "core/icons/cross/cross.svg";
import useOutsideClickHandler from "core/utils/useOutsideClickHandler";
import { isObject } from "core/utils/objectHelper";
import { SpacingStyle } from "core/styles/paddingMarginStyle";
import {
  Container,
  Header,
  Option,
  OptionsWrapper,
  ErrorMessage,
  OptionScrollerContainer,
} from "./_dropdownStyle";
import { useTheme } from "styled-components";

const Drawer = lazy(async () => {
  const module = await import("./_drawerStyle");
  return { default: module.Drawer };
});

export type DropdownOption = {
  id?: number | string;
  name?: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
};

export type OptionWrapperVariant = "default" | "drawer";

export type DropdownProps = SpacingStyle & {
  id?: string;
  name?: string;
  selectedIndex?: number | number[] | null;
  placeholder?: string | React.ReactNode;
  showSelected?: boolean;
  isMulti?: boolean;
  options?: string[] | number[] | DropdownOption[];
  optionAutoWidth?: boolean;
  selectedOptionIds?: (number | undefined)[];
  onClick?: (idx: number, option: any) => void;
  alignment?: "left" | "top" | "right";
  suffixIcon?: string;
  variant?: "default" | "borderless";
  height?: string;
  width?: string;
  disabled?: boolean;
  renderToggleIcon?: (showOption: boolean) => any;
  error?: string;
  errorPosition?: string;
  padding?: string;
  borderRadius?: number;
  textClamped?: boolean;
  "data-testid"?: string;
  customPlaceholder?: string | (() => string);
  onClose?: () => void;
  onDelete?: React.MouseEventHandler;
  prefixIcon?: string | React.ReactNode;
  emptyState?: React.ReactNode;
  openDropdown?: boolean;
  onOpenDropdown?: (value: boolean) => void;
  onClickHeader?: React.MouseEventHandler;
  optionStyle?: CSSProperties;
  optionsWrapperStyle?: CSSProperties;
  showCounter?: boolean;
  counter?: number;
  disableShowOptions?: boolean; // prevent dropdown to show options when click
  isToggleIcon?: boolean;
  enableSearch?: boolean;
  optionWrapperVariant?: OptionWrapperVariant;
};

const Dropdown = forwardRef(function (props: DropdownProps, ref) {
  const {
    placeholder,
    options,
    selectedIndex,
    selectedOptionIds,
    isMulti,
    showSelected,
    onClick,
    suffixIcon,
    alignment,
    variant,
    error,
    errorPosition,
    height,
    width,
    padding,
    borderRadius,
    textClamped,
    "data-testid": dataTestId,
    optionProps,
    renderToggleIcon,
    disabled,
    customPlaceholder,
    onClose,
    onDelete,
    prefixIcon,
    emptyState,
    openDropdown,
    onOpenDropdown,
    optionAutoWidth,
    onClickHeader,
    id,
    optionStyle,
    optionsWrapperStyle,
    showCounter,
    counter,
    disableShowOptions,
    isToggleIcon,
    enableSearch,
    optionWrapperVariant,
    ...rest
  } = props;

  const theme = useTheme();

  const titleRef = useRef<HTMLDivElement>();
  const mounted = useRef<boolean>(false);

  const [searchOption, setSearchOption] = useState("");

  const {
    ref: optionsRef,
    isComponentVisible: showOptions,
    setIsComponentVisible: setShowOptions,
  } = useOutsideClickHandler<HTMLDivElement>(openDropdown, [titleRef]);

  const optionClickHandler = (idx: number, option: { id: number }) => {
    if (!isMulti) setShowOptions(false);

    if (searchOption) setSearchOption("");

    onClick?.(idx, option);
  };

  const onDeleteOption = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(e);
  };

  const searchOptionRegex = new RegExp("^" + searchOption, "i");

  const filterSearchOptions =
    searchOption &&
    options?.filter((v) =>
      typeof v === "object"
        ? v?.name && searchOptionRegex.test(v?.name)
        : !!String(v) && searchOptionRegex.test(String(v))
    );

  const mappedOptions = searchOption ? filterSearchOptions : options;

  const toggleIcon =
    (isToggleIcon && renderToggleIcon?.(showOptions)) || (showOptions ? AngleUp : AngleDown);

  const showSelection =
    showSelected &&
    ((isMulti && (selectedIndex as any)?.length > 0) || (!isMulti && selectedIndex !== null));
  const isOptionObject = isObject(options?.[0]);
  const hasSelectedOption = Number(selectedIndex) > -1;
  const isDrawerVariant = optionWrapperVariant === "drawer";

  const placeholderText = () => {
    if (
      (showSelection && ((selectedIndex as number) > -1 || Array.isArray(selectedIndex))) ||
      Array.isArray(selectedOptionIds)
    ) {
      if (isMulti) {
        if (customPlaceholder) return customPlaceholder;

        const filteredOptions = (options as any).filter((v: any, idx: any) =>
          (selectedIndex as any).includes(idx)
        );
        const selectedOptions = isOptionObject
          ? filteredOptions?.map((v: any) => v?.label)
          : filteredOptions;

        return selectedOptions.join(", ");
      } else {
        const selectedOptions = options?.[selectedIndex as number];

        return isOptionObject ? (
          <>
            {(selectedOptions as any)?.icon}
            {(selectedOptions as any)?.placeholderLabel ||
              (selectedOptions as any)?.label ||
              placeholder}
          </>
        ) : (
          selectedOptions
        );
      }
    }

    return placeholder;
  };

  useEffect(() => {
    !showOptions && mounted.current && onClose && onClose();

    mounted.current = true;
  }, [showOptions]);

  const OptionWrapperController = useCallback(
    ({
      variant,
      DefaultWrapper,
      DrawerWrapper,
      children,
    }: {
      variant?: OptionWrapperVariant;
      DefaultWrapper: typeof OptionsWrapper;
      DrawerWrapper: typeof Drawer;
      children: React.ReactNode;
    }) =>
      variant === "drawer" ? (
        <Suspense fallback={null}>
          <DrawerWrapper
            show={showOptions}
            padding="0"
            closeable
            title="Search"
            onClose={() => setShowOptions(false)}>
            {children}
          </DrawerWrapper>
        </Suspense>
      ) : (
        <DefaultWrapper
          ref={optionsRef}
          minWidth={titleRef.current?.clientWidth}
          alignment={alignment}
          className="options-wrapper"
          style={optionsWrapperStyle}>
          {children}
        </DefaultWrapper>
      ),
    [optionWrapperVariant, showOptions]
  );

  return (
    <ThemeProvider theme={{ variant }}>
      <Container className="dropdown-container" id={id} {...rest}>
        <Header
          tabIndex={0}
          width={width}
          disabled={disabled}
          id={`${id}-header`}
          className="dropdown-header"
          activeBorder={showSelected && Array.isArray(selectedIndex) && selectedIndex?.length > 0}
          ref={mergeRefs([ref, titleRef])}
          onClick={(e) => {
            onOpenDropdown?.(!showOptions);
            !disableShowOptions && setShowOptions((prev) => !prev);
            onClickHeader?.(e);
          }}
          height={height}
          padding={padding}
          borderRadius={borderRadius}
          data-testid={dataTestId}
          isError={!!error}>
          <FlexContainer alignItems="center" gap={8} flexWrap={false}>
            {showCounter && <TextBold className="dropdown-counter">{counter}</TextBold>}
            {typeof prefixIcon === "string" ? (
              <Icon className="dropdown-prefix-icon" src={prefixIcon} size={16} />
            ) : (
              prefixIcon
            )}
            <TextBold
              className="dropdown-header-content"
              lineClamp={textClamped ? 1 : 0}
              color={!hasSelectedOption ? "neutral60" : "neutral90"}
              data-testid={`${dataTestId}-placeholder`}>
              {placeholderText()}
            </TextBold>
          </FlexContainer>
          {onDelete && hasSelectedOption && (
            <Icon
              className="clear-content-icon"
              src={CrossIcon}
              size={10}
              onClick={onDeleteOption}
            />
          )}
          {(toggleIcon || suffixIcon) && (
            <Icon className="dropdown-angle" src={toggleIcon || suffixIcon} width={14} ml={12} />
          )}
        </Header>
        {error && (
          <ErrorMessage errorPosition={errorPosition} data-testid={`${dataTestId}-error-message`}>
            {error}
          </ErrorMessage>
        )}
        {showOptions && (
          <OptionWrapperController
            variant={optionWrapperVariant}
            DefaultWrapper={OptionsWrapper}
            DrawerWrapper={Drawer}>
            {enableSearch && (
              <FlexContainer
                px={16}
                pt={isDrawerVariant ? 0 : 16}
                pb={16}
                borderBottom={`1px solid ${theme.colors.neutral.neutral40}`}>
                <TextField
                  search
                  searchPosition="start"
                  placeholder="Search"
                  value={searchOption}
                  onChange={(e) => setSearchOption(e.target.value)}
                />
              </FlexContainer>
            )}
            <OptionScrollerContainer optionWrapperVariant={optionWrapperVariant}>
              {!!mappedOptions?.length
                ? (alignment === "top" ? mappedOptions?.toReversed() : mappedOptions)?.map(
                    (option: any, idx: number) => {
                      const isOptionSelected =
                        (isMulti && Array.isArray(selectedIndex) && selectedIndex?.includes(idx)) ||
                        selectedOptionIds?.includes(option?.id);

                      return (
                        <span
                          key={idx}
                          style={{ cursor: option?.disabled ? "not-allowed" : "pointer" }}>
                          <Option
                            className={`options-item ${
                              isOptionSelected
                                ? "options-item-selected"
                                : option?.disabled
                                ? "options-item-disabled"
                                : ""
                            }`}
                            onClick={() =>
                              optionClickHandler(
                                enableSearch && searchOption
                                  ? options?.findIndex(
                                      (v) => v?.name === option?.name || v === option
                                    )
                                  : idx,
                                option
                              )
                            }
                            optionAutoWidth={optionAutoWidth}
                            disabled={option?.disabled}
                            selected={!!isOptionSelected}
                            data-testid={!!dataTestId ? `${dataTestId}-item` : null}
                            style={optionStyle}>
                            {option?.icon}

                            {isOptionObject ? option?.label : option}

                            {isOptionSelected && (
                              <Icon
                                size={16}
                                src={ChecklistIcon}
                                ml="auto"
                                className="autocomplete-checklist-icon"
                              />
                            )}
                          </Option>
                        </span>
                      );
                    }
                  )
                : emptyState ||
                  (enableSearch && (
                    <FlexContainer
                      alignItems="center"
                      justifyContent="center"
                      padding={16}
                      minHeight={isDrawerVariant ? "calc(100vh - 300px)" : "none"}>
                      <Text size={12}>
                        There’s no result for “<b>{searchOption}</b>”
                      </Text>
                    </FlexContainer>
                  ))}
            </OptionScrollerContainer>
          </OptionWrapperController>
        )}
      </Container>
    </ThemeProvider>
  );
});

Dropdown.defaultProps = {
  placeholder: "Select one",
  options: ["Dummy 1", "Dummy 2"],
  selectedIndex: null,
  showSelected: false,
  isMulti: false,
  onClick: () => {},
  alignment: "left",
  suffixIcon: AngleDown,
  variant: "borderless",
  height: "auto",
  width: "auto",
  isToggleIcon: true,
};

export default Dropdown;
